import {
  FormControl,
  Autocomplete,
  TextField,
  Divider,
  Typography,
} from '@mui/material';
import {
  FieldValues,
  FieldPath,
  useController,
} from 'react-hook-form';
import './index.scss';
import { TAutoCompleteProps } from '../formControllers/controllerTypes';

export function AutoCompleteControllerV2<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required,
  options = [],
  placeholder = `Select ${label?.toLowerCase()}`,
  error,
  disabled,
  onChange,
  isDefaultValueNull,
  disableClearable,
  disablePortal = true,
}: TAutoCompleteProps<TFieldValues, TFieldName>) {
  const { field } = useController({
    control,
    name,
    disabled,
  });

  const getSelectedValue = () => {
    return (
      options?.find(
        (ele) =>
          ele?.value?.toString().toLowerCase() ==
          field.value?.toString().toLowerCase()
      ) || null
    );
  };

  // Separate Internal and External Users
  const internalUsers = options.filter(
    (opt) => opt.type === 'internal'
  );
  const externalUsers = options.filter(
    (opt) => opt.type === 'external'
  );

  // Merge them while adding headers dynamically
  const sortedOptions = [
    ...(internalUsers.length > 0
      ? [{ label: 'Internal Users', type: 'header' }]
      : []),
    ...internalUsers,
    ...(internalUsers.length > 0 && externalUsers.length > 0
      ? [{ label: 'divider', type: 'divider' }]
      : []),
    ...(externalUsers.length > 0
      ? [{ label: 'External Users', type: 'header' }]
      : []),
    ...externalUsers,
  ];

  return (
    <FormControl
      fullWidth
      className="h-28 form-control_autocomplete">
      {label && (
        <label className="form-field-label">
          {label}
          {required && <i className="required">*</i>}
        </label>
      )}
      <Autocomplete
        className="custom-autocomplete"
        id={`auto-complete-${name}`}
        options={sortedOptions}
        disablePortal={disablePortal}
        {...field}
        value={getSelectedValue()}
        onChange={(_event, selectedOption) => {
          onChange
            ? onChange(selectedOption)
            : field.onChange(
                isDefaultValueNull
                  ? selectedOption?.value || null
                  : selectedOption?.value
              );
        }}
        renderOption={(props, option) => {
          if (option.type === 'header') {
            return (
              <Typography
                key={option.label}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '13px',
                  background: 'white',
                  padding: 2,
                }}>
                {option.label}
              </Typography>
            );
          }
          if (option.type === 'divider') {
            return (
              <Divider
                key="divider"
                sx={{ margin: '8px 0' }}
              />
            );
          }
          return (
            <li
              {...props}
              key={option.value?.toString()}
              style={{
                fontSize: '13px',
                color: option.disabled ? 'gray' : 'inherit',
                pointerEvents: option.disabled
                  ? 'none'
                  : 'auto',
                opacity: option.disabled ? 0.5 : 1,
              }}
              aria-disabled={option.disabled}>
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
          />
        )}
        disableClearable={disableClearable}
      />
      <p
        className={`form-field-error ${
          error ? 'visible' : ''
        }`}>
        {error?.toString()}
      </p>
    </FormControl>
  );
}
